import * as React from 'react';
import { useEffect, useState } from 'react';
import GlobalStyles from '@mui/joy/GlobalStyles';
import Avatar from '@mui/joy/Avatar';
import Box from '@mui/joy/Box';
import Divider from '@mui/joy/Divider';
import IconButton from '@mui/joy/IconButton';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton, { listItemButtonClasses } from '@mui/joy/ListItemButton';
import ListItemContent from '@mui/joy/ListItemContent';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import SupportRoundedIcon from '@mui/icons-material/SupportRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import yetiLogo from '../../images/yeticlean-logo.png';
import { closeSidebar, isMobile } from '../../utils';
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
    AdminPanelSettings,
    AutorenewRounded,
    BalanceOutlined,
    BookOutlined,
    CalendarMonthRounded,
    CallOutlined,
    CarRepair,
    ContactEmergencyRounded,
    CreditCard,
    CurrencyPoundOutlined,
    DashboardOutlined,
    EventRepeat,
    LocalPostOfficeOutlined,
    MarkEmailReadRounded,
    FormatPaintOutlined,
    Groups,
    MarkChatRead,
    MessageOutlined,
    MoneyRounded,
    Payment,
    PendingActionsOutlined,
    Person4,
    Settings,
    SmartToyRounded,
    StickyNote2Rounded,
    Voicemail
} from "@mui/icons-material";
import { Chip } from "@mui/joy";
import ColorSchemeToggle from "../ColorSchemeToggle";
import GeneralApiService, { GetUser, LogoutUser } from "../../axios/Axios";
import ApiService from "../../axios/Bookings-Axios";
import {hasOverdueUnpaidFranchiseFee} from "../../lib/franchise-fee-lock";
import { toast, ToastContainer } from "react-toastify";

interface User {
    first_name: string;
    last_name: string;
    username: string;
    location_permissions: string[];
    is_superuser?: boolean;
}

interface Franchise {
    name: string;
    gutter_ads_status: string;
    roof_ads_status: string;
}

function Toggler({
                     defaultExpanded = false,
                     renderToggle,
                     children,
                 }: {
    defaultExpanded?: boolean;
    children: React.ReactNode;
    renderToggle: (params: {
        open: boolean;
        setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    }) => React.ReactNode;
}) {
    const [open, setOpen] = useState(defaultExpanded);
    return (
        <React.Fragment>
            {renderToggle({ open, setOpen })}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateRows: open ? '1fr' : '0fr',
                    transition: '0.2s ease',
                    '& > *': {
                        overflow: 'hidden',
                    },
                }}
            >
                {children}
            </Box>
        </React.Fragment>
    );
}

const PERMISSIONS = {
    SUPER_ADMIN: 'super_admin',
    ADMIN: 'admin',
    EMPLOYEE: 'employee',
    SALES: 'sales',
};

export default function SideNav() {
    const location = useLocation();
    const navigate = useNavigate();
    const [loaded, setLoaded] = useState(false);
    const [user, setUser] = useState<User>({ first_name: '', last_name: '', username: '', location_permissions: [] });
    const [selectedFranchise, setFranchiseSelected] = useState<Franchise | undefined>();
    const [userPermissions, setUserPermissions] = useState<string[]>([]);
    const [hasPendingFees, setHasPendingFees] = useState(false);
    const [hasOverduePendingFees, setHasOverduePendingFees] = useState(false);
    const [needsReschedulingCount, setNeedsReschedulingCount] = useState(0);

    const selectedLocation = localStorage.getItem('location');

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (selectedLocation && selectedLocation !== 'All') {
                    const data = await GeneralApiService.getToggledAds();
                    setFranchiseSelected(data);
                } else {
                    setFranchiseSelected(undefined);
                }

                if (selectedLocation) {
                    const feesData = await GeneralApiService.getFranchiseFees();
                    if (Array.isArray(feesData)) {
                        setHasPendingFees(feesData.some(fee => fee.status !== 'paid'));
                        setHasOverduePendingFees(hasOverdueUnpaidFranchiseFee(feesData));
                    }
                } else {
                    setHasPendingFees(false);
                    setHasOverduePendingFees(false);
                }
                
                // Fetch needs rescheduling count (works for both specific location and "All")
                if (selectedLocation) {
                    try {
                        const reschedulingData = await ApiService.getNeedsReschedulingCount();
                        setNeedsReschedulingCount(reschedulingData?.count || 0);
                    } catch (err) {
                        console.error("Failed to fetch needs rescheduling count:", err);
                        setNeedsReschedulingCount(0);
                    }
                } else {
                    setNeedsReschedulingCount(0);
                }

                const response = await GetUser();
                if (response.status === 200) {
                    const permissions = response.data.location_permissions || [];
                    setUserPermissions(permissions);
                    setUser(response.data);
                }
            } catch (err) {
                console.error("Failed to fetch initial data for SideNav:", err);
            } finally {
                setLoaded(true);
            }
        };
        fetchData();
    }, [selectedLocation]);

    const isCurrentLocation = (locationCheck: string | string[]) => {
        if (Array.isArray(locationCheck)) {
            return locationCheck.includes(location.pathname);
        }
        return location.pathname === locationCheck;
    };

    const logout = () => {
        LogoutUser().then(() => navigate("/"));
    };

    const purchaseLeads = () => {
        navigate("/dashboard/lead-generation");
    };

    const isBookingsActive = location.pathname.startsWith('/dashboard/bookings') || location.pathname.startsWith('/dashboard/recurring-bookings');
    const isSuperAdmin = userPermissions.includes(PERMISSIONS.SUPER_ADMIN);
    const hasAdminPermission = userPermissions.includes(PERMISSIONS.ADMIN);
    const hasSalesPermission = userPermissions.includes(PERMISSIONS.SALES) || userPermissions.includes('sales_admin');
    const isSalesMode = selectedLocation === 'All' && hasSalesPermission && !isSuperAdmin;
    const isAdminMode = !isSalesMode && hasAdminPermission;
    const isEmployeeMode = !isSalesMode && !isAdminMode && userPermissions.includes(PERMISSIONS.EMPLOYEE);

    const isCrmRestrictedToFees = isAdminMode
        && !isSuperAdmin
        && !userPermissions.includes('franchise.location.all')
        && selectedLocation !== 'All'
        && hasOverduePendingFees;
    const lockedNavMessage = 'You need to pay your overdue franchise fees before using this CRM page.';
    const lockedNavButtonSx = isCrmRestrictedToFees ? {
        opacity: 0.42,
        cursor: 'not-allowed',
    } : {};
    const feeNavButtonSx = isCrmRestrictedToFees ? {
        opacity: 1,
        cursor: 'pointer',
    } : {};

    // Close sidebar on mobile when navigating
    const handleNavClick = () => {
        if (isMobile()) {
            closeSidebar();
        }
    };

    const handleLockedNavClick = (event: React.MouseEvent<HTMLElement>) => {
        if (!isCrmRestrictedToFees) {
            return;
        }

        const target = event.target as HTMLElement;
        const isFranchiseFeesLink = Boolean(target.closest('a[href="/dashboard/franchise-fees"]'));
        if (isFranchiseFeesLink) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        toast.error(lockedNavMessage, {
            containerId: 'crm-nav-lock',
            toastId: 'crm-nav-lock',
            autoClose: 5000,
            theme: 'dark',
            icon: <WarningAmberRounded />,
        });
    };

    const handleLockedNavHover = (event: React.MouseEvent<HTMLElement>) => {
        if (!isCrmRestrictedToFees) {
            return;
        }

        const target = event.target as HTMLElement;
        const navButton = target.closest('a') as HTMLElement | null;
        if (!navButton || navButton.getAttribute('href') === '/dashboard/franchise-fees') {
            return;
        }

        navButton.setAttribute('title', lockedNavMessage);
    };

    return (
        <>
        <ToastContainer containerId="crm-nav-lock" position="top-right" autoClose={5000} theme="dark" newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
        <Sheet
            className="Sidebar"
            sx={{
                position: 'fixed',
                transform: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))',
                transition: 'transform 0.4s, width 0.4s',
                zIndex: 10000,
                height: '100dvh',
                width: {
                    xs: '100vw', // Full width on mobile
                    sm: 'var(--Sidebar-width)',
                },
                maxWidth: {
                    xs: '100vw',
                    sm: 'var(--Sidebar-width)',
                },
                top: 0,
                p: 0,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                borderRight: '1px solid',
                borderColor: 'divider',
                boxSizing: 'border-box'
            }}
        >
            <GlobalStyles
                styles={(theme) => ({
                    ':root': {
                        '--Sidebar-width': '220px',
                        [theme.breakpoints.up('lg')]: {
                            '--Sidebar-width': '240px',
                        },
                    },
                })}
            />
            <Box
                className="Sidebar-overlay"
                sx={{
                    position: 'fixed',
                    zIndex: 9998,
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    opacity: 'var(--SideNavigation-slideIn)',
                    backgroundColor: 'var(--joy-palette-background-backdrop)',
                    transition: 'opacity 0.4s',
                    transform: {
                        // On mobile, show overlay when sidebar is open (translates from left of sidebar)
                        xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * 100vw))',
                        // On tablet/desktop, overlay appears next to sidebar
                        sm: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))',
                        // On large screens, hide overlay completely (sidebar is persistent)
                        lg: 'translateX(-100%)',
                    },
                }}
                onClick={() => closeSidebar()}
            />
            <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 2, pb: 1.5 }}>
                <img style={{ width: 'calc(100% - 40px)' }} src={yetiLogo} alt="Yeti Clean Logo" />
                {/* Close button - only visible on mobile */}
                <IconButton
                    onClick={() => closeSidebar()}
                    variant="plain"
                    color="neutral"
                    size="sm"
                    sx={{
                        display: { xs: 'flex', sm: 'none' },
                        position: 'absolute',
                        right: 8,
                        top: 8,
                    }}
                >
                    <CloseRoundedIcon />
                </IconButton>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, width: '100%', textAlign: 'center', justifyContent: 'center', px: 2, pb: 1.5 }}>
                <Typography level="title-md">{selectedLocation}</Typography>
            </Box>

            {isSalesMode && (
                <Box sx={{
                    minHeight: 0,
                    overflow: 'hidden auto',
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    px: 2,
                    [`& .${listItemButtonClasses.root}`]: { gap: 1.5 }
                }}>
                    <List
                        size="sm"
                        onClickCapture={handleLockedNavClick}
                        onMouseOverCapture={handleLockedNavHover}
                        sx={{
                            gap: 1,
                            [`& .${listItemButtonClasses.root}`]: {
                                ...lockedNavButtonSx,
                                '&.Mui-selected': {
                                    '& .MuiSvgIcon-root': {
                                        color: 'primary.plainColor',
                                    },
                                    '& .MuiTypography-root': {
                                        color: 'primary.plainColor',
                                    },
                                },
                            },
                        }}
                    >
                        <ListItem sx={{ marginTop: '5px' }}><ListItemContent><Typography level="body-sm" fontWeight="lg">Operations</Typography></ListItemContent></ListItem>
                        <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation(["/dashboard", "/dashboard/leads"])} component={Link} to="/dashboard/leads"><ContactEmergencyRounded /><ListItemContent><Typography level="title-sm">Leads</Typography></ListItemContent></ListItemButton></ListItem>
                        <ListItem nested>
                            <Toggler defaultExpanded={isBookingsActive} renderToggle={({ open, setOpen }) => (<ListItemButton onClick={() => setOpen(!open)} selected={isBookingsActive}><AssignmentRoundedIcon /><ListItemContent><Typography level="title-sm">Bookings</Typography></ListItemContent><KeyboardArrowDownIcon sx={{ transform: open ? 'rotate(180deg)' : 'none' }} /></ListItemButton>)}>
                                <List><ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/bookings')} component={Link} to="/dashboard/bookings"><StickyNote2Rounded /><ListItemContent><Typography level="title-sm">All Bookings</Typography></ListItemContent></ListItemButton></ListItem><ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/recurring-bookings')} component={Link} to="/dashboard/recurring-bookings"><AutorenewRounded /><ListItemContent><Typography level="title-sm">Recurring Bookings</Typography></ListItemContent></ListItemButton></ListItem></List>
                            </Toggler>
                        </ListItem>
                        <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/callback-reminders')} component={Link} to="/dashboard/callback-reminders"><PendingActionsOutlined /><ListItemContent><Typography level="title-sm">Callback Reminders</Typography></ListItemContent></ListItemButton></ListItem>
                        <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/todays-bookings')} component={Link} to="/dashboard/todays-bookings"><MarkChatRead /><ListItemContent><Typography level="title-sm">Todays Bookings</Typography></ListItemContent></ListItemButton></ListItem>
                        <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/calendar')} component={Link} to="/dashboard/calendar"><CalendarMonthRounded /><ListItemContent><Typography level="title-sm">Booking Calendar</Typography></ListItemContent></ListItemButton></ListItem>
                        <ListItem sx={{ marginTop: '5px' }}><ListItemContent><Typography level="body-sm" fontWeight="lg">Customer Management</Typography></ListItemContent></ListItem>
                        <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/customers')} component={Link} to="/dashboard/customers"><PersonRoundedIcon /><ListItemContent><Typography level="title-sm">Customers</Typography></ListItemContent></ListItemButton></ListItem>
                        <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/messages')} component={Link} to="/dashboard/messages"><MessageOutlined /><ListItemContent><Typography level="title-sm">Messages</Typography></ListItemContent></ListItemButton></ListItem>
                        <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/calls')} component={Link} to="/dashboard/calls"><CallOutlined /><ListItemContent><Typography level="title-sm">Call Log</Typography></ListItemContent></ListItemButton></ListItem>
                        <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/voicemails')} component={Link} to="/dashboard/voicemails"><Voicemail /><ListItemContent><Typography level="title-sm">Voicemails</Typography></ListItemContent></ListItemButton></ListItem>
                        <ListItem sx={{ marginTop: '5px' }}><ListItemContent><Typography level="body-sm" fontWeight="lg">Resources</Typography></ListItemContent></ListItem>
                        <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/knowledge-base')} component={Link} to="/dashboard/knowledge-base"><BookOutlined /><ListItemContent><Typography level="title-sm">Knowledge Base</Typography></ListItemContent></ListItemButton></ListItem>
                        <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation("/dashboard/mysales")} component={Link} to="/dashboard/mysales"><CurrencyPoundOutlined /><ListItemContent><Typography level="title-sm">MySales</Typography></ListItemContent></ListItemButton></ListItem>
                    </List>
                    <List size="sm" sx={{ mt: 'auto', flexGrow: 0, '--ListItem-radius': (theme) => theme.vars.radius.sm, '--List-gap': '8px', pb: 1 }}>
                        <ListItem><ListItemButton onClick={handleNavClick} component={Link} to="/select-location"><SupportRoundedIcon />Change Location</ListItemButton></ListItem>
                    </List>
                </Box>
            )}

            {isEmployeeMode && (
                <Box sx={{
                    minHeight: 0,
                    overflow: 'hidden auto',
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    px: 2,
                    [`& .${listItemButtonClasses.root}`]: { gap: 1.5 }
                }}>
                    <List
                        size="sm"
                        onClickCapture={handleLockedNavClick}
                        onMouseOverCapture={handleLockedNavHover}
                        sx={{
                            gap: 1,
                            [`& .${listItemButtonClasses.root}`]: {
                                ...lockedNavButtonSx,
                                '&.Mui-selected': {
                                    '& .MuiSvgIcon-root': {
                                        color: 'primary.plainColor',
                                    },
                                    '& .MuiTypography-root': {
                                        color: 'primary.plainColor',
                                    },
                                },
                            },
                        }}
                    >
                        <ListItem sx={{ marginTop: '5px' }}><ListItemContent><Typography level="body-sm" fontWeight="lg">Operations</Typography></ListItemContent></ListItem>
                        <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/todays-bookings')} component={Link} to="/dashboard/todays-bookings"><MarkChatRead /><ListItemContent><Typography level="title-sm">Todays Bookings</Typography></ListItemContent></ListItemButton></ListItem>
                        <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/calendar')} component={Link} to="/dashboard/calendar"><CalendarMonthRounded /><ListItemContent><Typography level="title-sm">Booking Calendar</Typography></ListItemContent></ListItemButton></ListItem>
                        <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/knowledge-base')} component={Link} to="/dashboard/knowledge-base"><BookOutlined /><ListItemContent><Typography level="title-sm">Knowledge Base</Typography></ListItemContent></ListItemButton></ListItem>
                    </List>
                </Box>
            )}

            {isAdminMode && (
                <>
                    {(isSuperAdmin || user.is_superuser) && (
                        <Box sx={{ px: 2, pt: 1, pb: 1.5, position: 'sticky', top: 0, bgcolor: 'background.surface', zIndex: 1 }}>
                            <List size="sm" onClickCapture={handleLockedNavClick} onMouseOverCapture={handleLockedNavHover}>
                                {isSuperAdmin && (
                                    <ListItem sx={{ mb: 1 }}>
                                        <ListItemButton
                                            onClick={handleNavClick}
                                            selected={isCurrentLocation('/dashboard/franchise-admin')}
                                            component={Link}
                                            to="/dashboard/franchise-admin"
                                            sx={{
                                                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
                                                border: '1px solid',
                                                borderColor: 'primary.outlinedBorder',
                                                boxShadow: 'sm',
                                                ...lockedNavButtonSx,
                                            }}
                                        >
                                            <AdminPanelSettings sx={{ color: 'primary.solidBg', fontSize: '1.25rem' }} />
                                            <ListItemContent>
                                                <Typography level="title-sm" fontWeight="lg">
                                                    Franchise Admin
                                                </Typography>
                                            </ListItemContent>
                                        </ListItemButton>
                                    </ListItem>
                                )}
                                {user.is_superuser && (
                                    <ListItem sx={{ mb: 0 }}>
                                        <ListItemButton
                                            onClick={handleNavClick}
                                            selected={isCurrentLocation('/dashboard/potential-zees')}
                                            component={Link}
                                            to="/dashboard/potential-zees"
                                            sx={{
                                                background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.12) 0%, rgba(14, 165, 233, 0.12) 100%)',
                                                border: '1px solid',
                                                borderColor: 'success.outlinedBorder',
                                                boxShadow: 'sm',
                                                ...lockedNavButtonSx,
                                            }}
                                        >
                                            <MarkEmailReadRounded sx={{ color: 'success.solidBg', fontSize: '1.25rem' }} />
                                            <ListItemContent>
                                                <Typography level="title-sm" fontWeight="lg">
                                                    Potential Zee&apos;s
                                                </Typography>
                                            </ListItemContent>
                                        </ListItemButton>
                                    </ListItem>
                                )}
                            </List>
                        </Box>
                    )}
                    <Box sx={{
                        minHeight: 0,
                        overflow: 'hidden auto',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        px: 2,
                        [`& .${listItemButtonClasses.root}`]: { gap: 1.5 }
                    }}>
                        <List
                            size="sm"
                            onClickCapture={handleLockedNavClick}
                            onMouseOverCapture={handleLockedNavHover}
                            sx={{
                                gap: 1,
                                '--List-nestedInsetStart': '30px',
                                '--ListItem-radius': (theme) => theme.vars.radius.sm,
                                [`& .${listItemButtonClasses.root}`]: {
                                    ...lockedNavButtonSx,
                                    '&[href="/dashboard/franchise-fees"]': feeNavButtonSx,
                                    '&.Mui-selected': {
                                        '& .MuiSvgIcon-root': {
                                            color: 'primary.plainColor',
                                        },
                                        '& .MuiTypography-root': {
                                            color: 'primary.plainColor',
                                        },
                                    },
                                },
                            }}
                        >
                            <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard')} component={Link} to="/dashboard"><DashboardOutlined /><ListItemContent><Typography level="title-sm">Home</Typography></ListItemContent></ListItemButton></ListItem>
                            <ListItem sx={{ marginTop: '5px' }}><ListItemContent><Typography level="body-sm" fontWeight="lg">Operations</Typography></ListItemContent></ListItem>
                            <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/leads')} component={Link} to="/dashboard/leads"><ContactEmergencyRounded /><ListItemContent><Typography level="title-sm">Leads</Typography></ListItemContent></ListItemButton></ListItem>
                            <ListItem nested>
                                <Toggler defaultExpanded={isBookingsActive} renderToggle={({ open, setOpen }) => (<ListItemButton onClick={() => setOpen(!open)} selected={isBookingsActive}><AssignmentRoundedIcon /><ListItemContent><Typography level="title-sm">Bookings</Typography></ListItemContent><KeyboardArrowDownIcon sx={{ transform: open ? 'rotate(180deg)' : 'none' }} /></ListItemButton>)}>
                                    <List><ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/bookings')} component={Link} to="/dashboard/bookings"><StickyNote2Rounded /><ListItemContent><Typography level="title-sm">All Bookings</Typography></ListItemContent></ListItemButton></ListItem><ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/recurring-bookings')} component={Link} to="/dashboard/recurring-bookings"><AutorenewRounded /><ListItemContent><Typography level="title-sm">Recurring Bookings</Typography></ListItemContent></ListItemButton></ListItem></List>
                                </Toggler>
                            </ListItem>
                            <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/callback-reminders')} component={Link} to="/dashboard/callback-reminders"><PendingActionsOutlined /><ListItemContent><Typography level="title-sm">Callback Reminders</Typography></ListItemContent></ListItemButton></ListItem>
                            <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/todays-bookings')} component={Link} to="/dashboard/todays-bookings"><MarkChatRead /><ListItemContent><Typography level="title-sm">Todays Bookings</Typography></ListItemContent></ListItemButton></ListItem>
                            <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/calendar')} component={Link} to="/dashboard/calendar"><CalendarMonthRounded /><ListItemContent><Typography level="title-sm">Booking Calendar</Typography></ListItemContent></ListItemButton></ListItem>
                            <ListItem>
                                <ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/needs-rescheduling')} component={Link} to="/dashboard/needs-rescheduling">
                                    <EventRepeat />
                                    <ListItemContent>
                                        <Typography level="title-sm">Needs Rescheduling</Typography>
                                    </ListItemContent>
                                    {needsReschedulingCount > 0 && (
                                        <Chip size="sm" color="warning" variant="solid">
                                            {needsReschedulingCount}
                                        </Chip>
                                    )}
                                </ListItemButton>
                            </ListItem>

                            <React.Fragment>
                                <ListItem sx={{ marginTop: '5px' }}><ListItemContent><Typography level="body-sm" fontWeight="lg">Franchise Management</Typography></ListItemContent></ListItem>
                                <ListItem>
                                    <ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/expenses')} component={Link} to="/dashboard/expenses">
                                        <MoneyRounded />
                                        <ListItemContent>
                                            <Typography level="title-sm">Expenses</Typography>
                                        </ListItemContent>
                                    </ListItemButton>
                                </ListItem>
                                <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/sales-team')} component={Link} to="/dashboard/sales-team"><Groups /><ListItemContent><Typography level="title-sm">Sales Team</Typography></ListItemContent></ListItemButton></ListItem>
                                <ListItem>
                                    <ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/leaflet-requests')} component={Link} to="/dashboard/leaflet-requests">
                                        <LocalPostOfficeOutlined />
                                        <ListItemContent>
                                            <Typography level="title-sm">Leaflet Downloads</Typography>
                                        </ListItemContent>
                                    </ListItemButton>
                                </ListItem>
                                {selectedLocation !== 'All' && (
                                    <>
                                        <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/franchise-settings')} component={Link} to="/dashboard/franchise-settings"><Settings /><ListItemContent><Typography level="title-sm">Franchise Settings</Typography></ListItemContent></ListItemButton></ListItem>
                                        <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/vans')} component={Link} to="/dashboard/vans"><CarRepair /><ListItemContent><Typography level="title-sm">Vans Schedule</Typography></ListItemContent></ListItemButton></ListItem>
                                        <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/team')} component={Link} to="/dashboard/team"><Person4 /><ListItemContent><Typography level="title-sm">Team</Typography></ListItemContent></ListItemButton></ListItem>
                                        <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/lead-generation')} component={Link} to="/dashboard/lead-generation"><BalanceOutlined /><ListItemContent><Typography level="title-sm">Marketing Balance</Typography></ListItemContent></ListItemButton></ListItem>
                                    </>
                                )}
                                <ListItem>
                                    <ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/franchise-fees')} component={Link} to="/dashboard/franchise-fees" sx={feeNavButtonSx}>
                                        <Payment />
                                        <ListItemContent>
                                            <Typography level="title-sm">Franchise Fees</Typography>
                                        </ListItemContent>
                                        {hasPendingFees && (
                                            <Chip size="sm" color="danger" variant="solid">
                                                Pending
                                            </Chip>
                                        )}
                                    </ListItemButton>
                                </ListItem>
                            </React.Fragment>

                            <ListItem sx={{ marginTop: '5px' }}><ListItemContent><Typography level="body-sm" fontWeight="lg">Customer Management</Typography></ListItemContent></ListItem>
                            <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/customers')} component={Link} to="/dashboard/customers"><PersonRoundedIcon /><ListItemContent><Typography level="title-sm">Customers</Typography></ListItemContent></ListItemButton></ListItem>
                            <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/messages')} component={Link} to="/dashboard/messages"><MessageOutlined /><ListItemContent><Typography level="title-sm">Messages</Typography></ListItemContent></ListItemButton></ListItem>
                            <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/calls')} component={Link} to="/dashboard/calls"><CallOutlined /><ListItemContent><Typography level="title-sm">Call Log</Typography></ListItemContent></ListItemButton></ListItem>
                            <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/voicemails')} component={Link} to="/dashboard/voicemails"><Voicemail /><ListItemContent><Typography level="title-sm">Voicemails</Typography></ListItemContent></ListItemButton></ListItem>
                            {userPermissions.includes(PERMISSIONS.SUPER_ADMIN) && (
                                <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/ronald')} component={Link} to="/dashboard/ronald"><SmartToyRounded /><ListItemContent><Typography level="title-sm">Ronald AI</Typography></ListItemContent></ListItemButton></ListItem>
                            )}

                            {selectedLocation !== 'All' && (
                                <React.Fragment>
                                    <ListItem sx={{ marginTop: '5px' }}><ListItemContent><Typography level="body-sm" fontWeight="lg">Customer Invoices</Typography></ListItemContent></ListItem>
                                    <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/invoices')} component={Link} to="/dashboard/invoices"><CreditCard /><ListItemContent><Typography level="title-sm">Customer Invoices</Typography></ListItemContent></ListItemButton></ListItem>
                                    <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/payouts')} component={Link} to="/dashboard/payouts"><MoneyRounded /><ListItemContent><Typography level="title-sm">Your Payouts</Typography></ListItemContent></ListItemButton></ListItem>
                                </React.Fragment>
                            )}

                            <ListItem sx={{ marginTop: '5px' }}><ListItemContent><Typography level="body-sm" fontWeight="lg">Resources</Typography></ListItemContent></ListItem>
                            <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/knowledge-base')} component={Link} to="/dashboard/knowledge-base"><BookOutlined /><ListItemContent><Typography level="title-sm">Knowledge Base</Typography></ListItemContent></ListItemButton></ListItem>
                            {hasSalesPermission && (
                                <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation("/dashboard/mysales")} component={Link} to="/dashboard/mysales"><CurrencyPoundOutlined /><ListItemContent><Typography level="title-sm">MySales</Typography></ListItemContent></ListItemButton></ListItem>
                            )}
                            <ListItem sx={{ marginTop: '5px' }}><ListItemContent><Typography level="body-sm" fontWeight="lg">Social Media</Typography></ListItemContent></ListItem>
                            <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/media')} component={Link} to="/dashboard/media"><FormatPaintOutlined /><ListItemContent><Typography level="title-sm">Media</Typography></ListItemContent></ListItemButton></ListItem>
                        </List>
                        <List size="sm" sx={{ mt: 'auto', flexGrow: 0, '--ListItem-radius': (theme) => theme.vars.radius.sm, '--List-gap': '8px', pb: 1 }}>
                            {selectedLocation !== 'All' && selectedFranchise &&
                                <ListItem sx={{ m: 0, p: 0, width: '100%' }}>
                                    <Box sx={{ justifyContent: 'center', width: '100%', alignItems: 'center' }}>
                                        <Box onClick={() => { if (!isCrmRestrictedToFees) { handleNavClick(); purchaseLeads(); } }} sx={{ whiteSpace: 'no-wrap', marginTop: '5px', marginBottom: '5px', cursor: isCrmRestrictedToFees ? 'default' : 'pointer', opacity: isCrmRestrictedToFees ? 0.42 : 1, display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ whiteSpace: 'nowrap' }} level="title-sm">RC Ads:</Typography>
                                            <Chip sx={{ width: '100%', overflow: 'none' }} color={selectedFranchise.roof_ads_status === 'Off' ? 'danger' : selectedFranchise.roof_ads_status === 'Pending' ? 'warning' : 'success'}>
                                                {selectedFranchise.roof_ads_status}
                                            </Chip>
                                        </Box>
                                    </Box>
                                </ListItem>
                            }
                            <ListItem><ListItemButton onClick={handleNavClick} component={Link} to="/select-location"><SupportRoundedIcon />Change Location</ListItemButton></ListItem>
                        </List>
                    </Box>
                </>
            )}

            {isSuperAdmin && !isAdminMode && (
                <Box sx={{ minHeight: 0, overflow: 'hidden auto', flexGrow: 1, display: 'flex', flexDirection: 'column', pr: 2, pl: 2 }}>
                    <List size="sm" sx={{ gap: 1, '--ListItem-radius': (theme) => theme.vars.radius.sm, '--List-nestedInsetStart': '30px' }}>
                        <ListItem sx={{ marginTop: '5px' }}><ListItemContent><Typography level="body-sm" fontWeight="lg">Yeti HQ</Typography></ListItemContent></ListItem>
                        <ListItem><ListItemButton onClick={handleNavClick} selected={isCurrentLocation('/dashboard/ronald')} component={Link} to="/dashboard/ronald"><SmartToyRounded /><ListItemContent><Typography level="title-sm">Ronald AI</Typography></ListItemContent></ListItemButton></ListItem>
                    </List>
                </Box>
            )}

            <Divider sx={{ mx: 2 }} />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 2 }}>
                <Avatar variant="outlined" size="sm" />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography level="title-sm">{user.username}</Typography>
                </Box>
                <ColorSchemeToggle sx={{ ml: 'auto' }} />
                <IconButton size="sm" variant="outlined" color="neutral" onClick={logout}>
                    <LogoutRoundedIcon />
                </IconButton>
            </Box>
        </Sheet>
        </>
    );
}